import { Tooltip, TooltipContent, TooltipTrigger } from '@plato/ui/primitives/tooltip';
import {
    CalculatorIcon,
    CalendarRangeIcon,
    ChartColumnIcon,
    LanguagesIcon,
    LogOutIcon,
    SettingsIcon,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { useSignOut } from '../../../lib/hooks/use-sign-out.ts';
import { applyLanguage } from '../../../lib/i18n/i18n.ts';
import { languageConfigs, supportedLanguages } from '../../../lib/i18n/language-config.ts';
import { metricDefinitionsNamespace } from '../../../lib/metric-definition-keys.ts';
import type { DefaultMetric, DefaultTimeframe } from '../../../server/utils/explorer-utils.ts';
import { VisibilityWindow } from '../../../server/utils/explorer-utils.ts';
import { api } from '../../../utils/api.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { Can } from '../../acl/can.tsx';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { CustomerModeToggle } from '../../customer-mode/components/customer-mode-toggle.tsx';
import { DialogStateManager } from '../../dialog/dialog-provider.tsx';
import { MetricExplainerDialog } from '../../explorer/components/metric-explainer.tsx';
import { ImpersonationBanner } from './impersonation-banner.tsx';
import {
    Menu,
    MenuActionRow,
    MenuBackRow,
    MenuDrillRow,
    MenuOptionRow,
    menuSeparatorClassName,
} from './menu-primitives.tsx';
import { PushStack } from './push-stack.tsx';
import { ViewAsRow, ViewAsRows } from './view-as-menu.tsx';

/**
 * Which setting the menu has drilled into, or `undefined` for the settings level
 * itself. Both surfaces (sidebar pop-over, mobile sheet) share these names so the
 * levels below compose the same way in each. The order is the order the pushed
 * levels sit in, so each surface derives its level index from the name instead
 * of hardcoding one per sub-level.
 */
export const settingsSubLevels = ['language', 'defaultMetric', 'defaultTimeframe', 'viewAs'] as const;

export type SettingsSubLevel = (typeof settingsSubLevels)[number];

export type LanguageLevelProps = {
    onBack: () => void;
};

/** The pushed language level: back header + one row per supported language. */
export function LanguageLevel({ onBack }: LanguageLevelProps) {
    const { t, language } = useLocale('common');
    const sendEvent = useAnalytics();
    const utils = api.useUtils();
    const setLanguage = api.platoUser.command.settings.update.useMutation({
        onMutate: ({ preferredLanguage: next }) => {
            utils.platoUser.me.getMe.setData(undefined, (me) =>
                me && next ? { ...me, language: next } : me,
            );
        },
        onSettled: () => utils.platoUser.me.getMe.invalidate(),
    });

    return (
        <Menu className="p-0">
            <MenuBackRow label={t('settings__language_label')} onBack={onBack} />
            {supportedLanguages.map((supported) => (
                <MenuOptionRow
                    key={supported}
                    label={languageConfigs[supported].nativeName}
                    selected={supported === language}
                    onSelect={() => {
                        if (supported !== language) {
                            sendEvent('language_switched', {
                                from: language,
                                to: supported,
                                source: 'settings_menu',
                            });
                            setLanguage.mutate({ preferredLanguage: supported });
                        }
                        applyLanguage(supported);
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

export type CommercialDefaultLevelProps = {
    onBack: () => void;
};

/** The pushed default-figure level: back header + one row per metric. */
export function DefaultMetricLevel({ onBack }: CommercialDefaultLevelProps) {
    const { t } = useLocale('common');
    const { defaultMetric } = useAppConfig();
    const sendEvent = useAnalytics();
    const utils = api.useUtils();

    // `AppConfig` carries the effective default — this user's pick where they
    // made one, the tenant's otherwise — so the write only shows up in the
    // menu once that query has refetched.
    const { mutate } = api.platoUser.command.settings.update.useMutation({
        onSuccess: async () => {
            await utils.platoUser.query.appConfig.getAppConfig.invalidate();
        },
    });

    const options: { value: DefaultMetric; label: string }[] = [
        { value: 'Revenue', label: t('settings__metric_revenue') },
        { value: 'Order_Sum', label: t('settings__metric_order_sum') },
    ];

    return (
        <Menu className="p-0">
            <MenuBackRow label={t('settings__default_metric_label')} onBack={onBack} />
            {options.map(({ value, label }) => (
                <MenuOptionRow
                    key={value}
                    label={label}
                    selected={value === defaultMetric}
                    onSelect={() => {
                        if (value !== defaultMetric) {
                            sendEvent('commercial_default_changed', {
                                setting: 'metric',
                                from: defaultMetric,
                                to: value,
                            });
                            mutate({ preferredMetric: value });
                        }
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

/** The pushed default-period level: back header + one row per timeframe. */
export function DefaultTimeframeLevel({ onBack }: CommercialDefaultLevelProps) {
    const { t } = useLocale('common');
    const { defaultTimeframe } = useAppConfig();
    const sendEvent = useAnalytics();
    const utils = api.useUtils();

    const { mutate } = api.platoUser.command.settings.update.useMutation({
        onSuccess: () => {
            void utils.platoUser.query.appConfig.getAppConfig.invalidate();
        },
    });

    const options: { value: DefaultTimeframe; label: string }[] = [
        { value: 'YTD', label: t('settings__timeframe_ytd') },
        { value: 'TTM', label: t('settings__timeframe_ttm') },
    ];

    return (
        <Menu className="p-0">
            <MenuBackRow label={t('settings__default_timeframe_label')} onBack={onBack} />
            {options.map(({ value, label }) => (
                <MenuOptionRow
                    key={value}
                    label={label}
                    selected={value === defaultTimeframe}
                    onSelect={() => {
                        if (value !== defaultTimeframe) {
                            sendEvent('commercial_default_changed', {
                                setting: 'timeframe',
                                from: defaultTimeframe,
                                to: value,
                            });
                            mutate({ preferredTimeframe: value });
                        }
                        onBack();
                    }}
                />
            ))}
        </Menu>
    );
}

export type ViewAsLevelProps = {
    onBack: () => void;
    /** Called after an actual view switch, so the surface can dismiss entirely. */
    onSwitched: () => void;
};

/** The pushed "View as" level: back header + Manager / Sales Rep. */
export function ViewAsLevel({ onBack, onSwitched }: ViewAsLevelProps) {
    const { t } = useLocale('common');

    return (
        <>
            <Menu className="p-0">
                <MenuBackRow label={t('view_switch__view_as_label')} onBack={onBack} />
            </Menu>
            <ViewAsRows onPicked={onBack} onSwitched={onSwitched} />
        </>
    );
}

export type SettingsRowsProps = {
    onOpenLevel: (level: SettingsSubLevel) => void;
    /** Called when a row completes an action that should dismiss the surface. */
    onClose?: () => void;
};

/**
 * The settings level. Customer view leads because it is the one setting reps
 * change day to day. The drill-in settings follow as collapsed
 * "label … value" rows, then the metric explainer as the one reference entry,
 * and the account actions sit below a separator.
 */
export function SettingsRows({ onOpenLevel, onClose }: SettingsRowsProps) {
    const { t, language } = useLocale('common');
    const { signOutFn } = useSignOut();
    const sendEvent = useAnalytics();
    const { enableDefaultMetricAndTimeframe, defaultMetric, defaultTimeframe } = useAppConfig();

    const metricLabel =
        defaultMetric === 'Order_Sum' ? t('settings__metric_order_sum') : t('settings__metric_revenue');
    const timeframeLabel =
        defaultTimeframe === VisibilityWindow.TTM
            ? t('settings__timeframe_ttm')
            : t('settings__timeframe_ytd');

    return (
        <div className="flex w-full flex-col">
            <Can I="read" a="Stats" field="total_margin">
                <div className="py-tight">
                    <CustomerModeToggle />
                </div>
            </Can>

            <Menu className="p-0">
                <MenuDrillRow
                    icon={LanguagesIcon}
                    label={t('settings__language_label')}
                    value={languageConfigs[language].nativeName}
                    onOpen={() => {
                        onOpenLevel('language');
                    }}
                />

                {enableDefaultMetricAndTimeframe && (
                    <>
                        <MenuDrillRow
                            icon={ChartColumnIcon}
                            label={t('settings__default_metric_label')}
                            value={metricLabel}
                            onOpen={() => {
                                onOpenLevel('defaultMetric');
                            }}
                        />

                        <MenuDrillRow
                            icon={CalendarRangeIcon}
                            label={t('settings__default_timeframe_label')}
                            value={timeframeLabel}
                            onOpen={() => {
                                onOpenLevel('defaultTimeframe');
                            }}
                        />
                    </>
                )}

                {/* Renders nothing for non-managers. */}
                <ViewAsRow
                    onOpen={() => {
                        onOpenLevel('viewAs');
                    }}
                />

                <MenuActionRow
                    icon={CalculatorIcon}
                    label={t('title', { ns: metricDefinitionsNamespace })}
                    onSelect={() => {
                        sendEvent('metric_explainer_opened', { source: 'settings_menu' });
                        void DialogStateManager.show(MetricExplainerDialog);
                    }}
                />
            </Menu>

            <div className={menuSeparatorClassName} />

            {/* Renders nothing unless a super-admin is impersonating. */}
            <div className="empty:hidden">
                <ImpersonationBanner expanded />
            </div>

            <Button
                variant="ghost"
                className="w-full justify-start text-content"
                leftIcon={LogOutIcon}
                onClick={() => {
                    onClose?.();
                    signOutFn();
                }}
            >
                <span className="font-normal">{t('logout__button')}</span>
            </Button>
        </div>
    );
}

/**
 * Sidebar settings entry point: everything that is not navigation or support —
 * customer view, language, the current view mode, how the figures are
 * calculated, exit demo, log out. Opens as a pop-over whose contents push
 * sideways into each setting instead of stacking a second surface on top.
 *
 * The sidebar only exists at `sm` and up. Below that the mobile bottom nav
 * owns the same levels inside its own sheet.
 */
export function SettingsMenu({ expanded = true }: { expanded?: boolean }) {
    const { t } = useLocale('common');
    const sendEvent = useAnalytics();
    const [open, setOpen] = useState(false);
    const [subLevel, setSubLevel] = useState<SettingsSubLevel | undefined>(undefined);

    // Resets subLevel itself rather than relying on onOpenChange: Radix only
    // calls onOpenChange for its own interaction sources (outside click,
    // Escape, trigger), never when `open` is driven programmatically like
    // this is after a view switch — so that path needs its own reset too.
    const close = (): void => {
        setOpen(false);
        setSubLevel(undefined);
    };
    const backToSettings = (): void => {
        setSubLevel(undefined);
    };

    // Reset to the settings level on close so reopening never resumes mid-drill.
    const onOpenChange = (next: boolean): void => {
        setOpen(next);
        if (next) {
            sendEvent('settings_menu_opened', { surface: expanded ? 'sidebar' : 'rail' });
        } else {
            setSubLevel(undefined);
        }
    };

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            {expanded ? (
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        className="w-full justify-start text-content"
                        leftIcon={SettingsIcon}
                    >
                        <span className="font-normal">{t('settings__button')}</span>
                    </Button>
                </PopoverTrigger>
            ) : (
                <Tooltip>
                    {/* The tooltip attaches to a wrapper rather than to the button
                        itself. Rendering the tooltip trigger as the PopoverTrigger
                        makes the outer trigger's props win, which shadows the
                        popover's click handler and leaves the menu unopenable. */}
                    <TooltipTrigger render={<span className="inline-flex" />}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon-s"
                                aria-label={t('settings__button')}
                                icon={SettingsIcon}
                            />
                        </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="right">{t('settings__button')}</TooltipContent>
                </Tooltip>
            )}
            <PopoverContent
                side="right"
                align="end"
                className="z-50 w-[320px] p-cozy"
                onOpenAutoFocus={(event) => {
                    event.preventDefault();
                    if (event.target instanceof HTMLElement) {
                        event.target.focus();
                    }
                }}
            >
                <PushStack
                    // Level 0 is the settings level; the rest follow
                    // `settingsSubLevels`, so the two orders must stay in step.
                    activeIndex={subLevel === undefined ? 0 : settingsSubLevels.indexOf(subLevel) + 1}
                    levels={[
                        {
                            key: 'settings',
                            content: <SettingsRows onOpenLevel={setSubLevel} onClose={close} />,
                        },
                        {
                            key: 'language',
                            content: <LanguageLevel onBack={backToSettings} />,
                        },
                        {
                            key: 'defaultMetric',
                            content: <DefaultMetricLevel onBack={backToSettings} />,
                        },
                        {
                            key: 'defaultTimeframe',
                            content: <DefaultTimeframeLevel onBack={backToSettings} />,
                        },
                        {
                            key: 'viewAs',
                            content: <ViewAsLevel onBack={backToSettings} onSwitched={close} />,
                        },
                    ]}
                />
            </PopoverContent>
        </Popover>
    );
}
